"use client"

import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';
import { auth } from '@/lib/firebase';
import { useTransaction } from './TransactionContext';
import { usePayroll } from './PayrollContext';
import { useTaxManager } from './TaxManagerContext';
import { useInventory } from './InventoryContext';
import { useInvoice } from './InvoiceContext';
import { useBudget } from './BudgetContext';
import { useBudgetManager } from './BudgetManagerContext';
import { useAccount } from './AccountContext';
import JSZip from 'jszip';

// Define types
export type ExportPeriod = 'all' | 'monthly' | 'quarterly' | 'yearly';
export type DataCollection = 'transactions' | 'payroll' | 'taxes' | 'inventory' | 'invoices' | 'budget';
export type ExportFileType = 'json' | 'csv' | 'zip';

export type ExportConfig = {
    collections: DataCollection[];
    period: ExportPeriod;
    startDate?: Date;
    endDate?: Date;
    budgetId?: string;
    fileType: ExportFileType;
};

type ExportState = {
    config: ExportConfig;
    loading: boolean;
    error: string | null;
    exportedData: any | null;
};

type ExportAction =
    | { type: 'SET_CONFIG'; payload: Partial<ExportConfig> }
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_ERROR'; payload: string | null }
    | { type: 'SET_EXPORTED_DATA'; payload: any };

const initialState: ExportState = {
    config: {
        collections: [],
        period: 'all',
        fileType: 'json'
    },
    loading: false,
    error: null,
    exportedData: null,
};

type ExportContextType = {
    state: ExportState;
    updateConfig: (config: Partial<ExportConfig>) => void;
    exportData: (fileType: ExportFileType) => Promise<void>;
    clearError: () => void;
};

const ExportContext = createContext<ExportContextType | undefined>(undefined);

// Category symbols mapping
const categorySymbols: { [key: string]: string } = {
    'foodDining': '🍽️',
    'transportation': '🚗',
    'utilities': '🔌',
    'entertainment': '🎬',
    'shopping': '🛒',
    'health': '⚕️',
    'travel': '✈️',
    'education': '🎓',
    'gifts': '🎁',
    'phone': '📱',
    'internet': '🛜',
    'groceries': '🛒',
    'rental': '🏠',
    'sales': '🧾',
    'income': '💰',
    'expense': '💸'
};

function exportReducer(state: ExportState, action: ExportAction): ExportState {
    switch (action.type) {
        case 'SET_CONFIG':
            return { ...state, config: { ...state.config, ...action.payload } };
        case 'SET_LOADING':
            return { ...state, loading: action.payload };
        case 'SET_ERROR':
            return { ...state, error: action.payload };
        case 'SET_EXPORTED_DATA':
            return { ...state, exportedData: action.payload };
        default:
            return state;
    }
}

// Utility functions
const flattenObject = (obj: any, prefix = ''): Record<string, string> => {
    return Object.keys(obj).reduce((acc: Record<string, string>, k: string) => {
        const pre = prefix.length ? prefix + '.' : '';
        if (typeof obj[k] === 'object' && obj[k] !== null && !(obj[k] instanceof Date)) {
            Object.assign(acc, flattenObject(obj[k], pre + k));
        } else {
            acc[pre + k] = obj[k] instanceof Date ? obj[k].toISOString() : String(obj[k]);
        }
        return acc;
    }, {});
};

const convertToCSV = (data: any[]): string => {
    if (data.length === 0) return '';

    const flattenedFirst = flattenObject(data[0]);
    const headers = Object.keys(flattenedFirst);

    const rows = data.map(obj => {
        const flatObj = flattenObject(obj);
        return headers.map(header => {
            const value = flatObj[header] ?? '';
            return value.includes(',') ? `"${value.replace(/"/g, '""')}"` : value;
        }).join(',');
    });

    return [headers.join(','), ...rows].join('\n');
};

const createAndDownloadZip = async (
    files: { name: string; content: string }[],
    zipFileName: string
) => {
    const zip = new JSZip();

    files.forEach(file => {
        zip.file(file.name, file.content);
    });

    const metadata = {
        exportDate: new Date().toISOString(),
        fileCount: files.length,
        files: files.map(f => f.name)
    };
    zip.file('metadata.json', JSON.stringify(metadata, null, 2));

    const content = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: {
            level: 9
        }
    });

    const url = URL.createObjectURL(content);
    const a = document.createElement('a');
    a.href = url;
    a.download = zipFileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

export function ExportProvider({ children }: { children: ReactNode }) {
    const [state, dispatch] = useReducer(exportReducer, initialState);
    const { state: transactionState } = useTransaction();
    const { state: payrollState } = usePayroll();
    const { state: taxState } = useTaxManager();
    const { state: inventoryState } = useInventory();
    const { state: invoiceState } = useInvoice();
    const { state: budgetState } = useBudget();
    const { state: budgetManagerState } = useBudgetManager();
    const { state: accountState } = useAccount();

    // Helper functions
    const getAccountDetails = useCallback((accountId: string) => {
        const account = accountState.accounts.find(a => a.id === accountId);
        return account ? {
            name: account.name,
            type: account.type,
            color: account.color,
            balance: account.balance,
            availableToBudget: account.availableToBudget
        } : { name: 'Unknown Account', type: 'Unknown', color: '#000000', balance: 0, availableToBudget: 0 };
    }, [accountState.accounts]);

    const getBudgetItemDetails = useCallback((budgetItemId: string) => {
        if (!budgetItemId) return null;
        const [category, name] = budgetItemId.split('_');
        const budgetItem = budgetState.budgetItems.find(
            item => item.category === category && item.name === name
        );
        return budgetItem ? {
            name: budgetItem.name,
            category: budgetItem.category,
            targetAmount: budgetItem.targetAmount,
            amount: budgetItem.amount,
            spent: budgetItem.spent,
            symbol: budgetItem.symbol || categorySymbols[budgetItem.name] || '📌'
        } : null;
    }, [budgetState.budgetItems]);

    const getInventoryItemDetails = useCallback((itemId: string) => {
        const item = inventoryState.items.find(i => i.id === itemId);
        return item ? {
            name: item.name,
            category: item.category,
            price: item.price,
            quantity: item.quantity
        } : { name: 'Unknown Item', category: 'Unknown', price: 0, quantity: 0 };
    }, [inventoryState.items]);

    // Data enrichment functions
    const enrichTransactionData = useCallback((transaction: any) => {
        const accountDetails = getAccountDetails(transaction.accountId);
        const budgetItemDetails = getBudgetItemDetails(transaction.budgetItemId);

        return {
            description: transaction.description,
            amount: transaction.amount,
            date: transaction.date,
            type: transaction.type,
            category: {
                name: transaction.category,
                parent: transaction.parentCategory,
                symbol: categorySymbols[transaction.category] || '📌'
            },
            account: accountDetails,
            budgetItem: budgetItemDetails,
            createdAt: transaction.createdAt,
            updatedAt: transaction.updatedAt
        };
    }, [getAccountDetails, getBudgetItemDetails]);

    const enrichInvoiceData = useCallback((invoice: any) => {
        const accountDetails = getAccountDetails(invoice.accountId);
        const enrichedItems = invoice.items.map((item: any) => {
            const inventoryDetails = getInventoryItemDetails(item.inventoryItemId);
            return {
                ...item,
                item: inventoryDetails,
                inventoryItemId: undefined
            };
        });

        return {
            invoiceNumber: invoice.invoiceNumber,
            customerName: invoice.customerName,
            customerEmail: invoice.customerEmail,
            dueDate: invoice.dueDate,
            status: invoice.status,
            amount: invoice.amount,
            taxRate: invoice.taxRate,
            notes: invoice.notes,
            items: enrichedItems,
            account: accountDetails,
            createdAt: invoice.createdAt,
            updatedAt: invoice.updatedAt
        };
    }, [getAccountDetails, getInventoryItemDetails]);

    const enrichPayrollData = useCallback((payroll: any) => {
        const accountDetails = getAccountDetails(payroll.accountId);
        return {
            employeeName: payroll.employeeName,
            employeeId: payroll.employeeId,
            payPeriod: {
                start: payroll.payPeriodStart,
                end: payroll.payPeriodEnd
            },
            hours: {
                regular: payroll.hoursWorked,
                overtime: payroll.overtimeHours
            },
            rates: {
                hourly: payroll.hourlyRate,
                overtime: payroll.overtimeRate
            },
            pay: {
                gross: payroll.grossPay,
                deductions: payroll.deductions,
                net: payroll.netPay
            },
            paymentDate: payroll.paymentDate,
            status: payroll.status,
            account: accountDetails,
            createdAt: payroll.createdAt,
            updatedAt: payroll.updatedAt
        };
    }, [getAccountDetails]);

    const enrichBudgetData = useCallback((budgetItem: any) => {
        return {
            name: budgetItem.name,
            category: budgetItem.category,
            symbol: budgetItem.symbol || categorySymbols[budgetItem.name] || '📌',
            amounts: {
                current: budgetItem.amount,
                target: budgetItem.targetAmount,
                spent: budgetItem.spent,
                remaining: budgetItem.targetAmount - budgetItem.spent
            },
            date: budgetItem.date
        };
    }, []);

    const filterDataByPeriod = useCallback((data: any[], dateField: string) => {
        const { period, startDate, endDate } = state.config;

        if (period === 'all') return data;

        return data.filter(item => {
            const itemDate = new Date(item[dateField]);

            if (startDate && itemDate < startDate) return false;
            if (endDate && itemDate > endDate) return false;

            return true;
        });
    }, [state.config]);

    const filterDataByUserAndBudget = useCallback((data: any[]) => {
        const user = auth.currentUser;
        const currentBudget = budgetManagerState.currentBudget;

        if (!user || !currentBudget) {
            throw new Error('User must be authenticated and budget must be selected');
        }

        return data.filter(item =>
            item.createdBy === user.uid &&
            item.budgetId === currentBudget.id
        );
    }, [budgetManagerState.currentBudget]);

    // Core functions
    const updateConfig = useCallback((config: Partial<ExportConfig>) => {
        dispatch({ type: 'SET_CONFIG', payload: config });
    }, []);

    const getExportFileName = useCallback((fileType: ExportFileType) => {
        const currentBudget = budgetManagerState.currentBudget;
        const date = new Date().toISOString().split('T')[0];
        const budgetName = currentBudget?.name.toLowerCase().replace(/\s+/g, '-') || 'unknown-budget';
        return `${budgetName}-export-${date}.${fileType}`;
    }, [budgetManagerState.currentBudget]);

    const exportData = useCallback(async (fileType: ExportFileType) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const user = auth.currentUser;
            const currentBudget = budgetManagerState.currentBudget;

            if (!user || !currentBudget) {
                throw new Error('User must be authenticated and budget must be selected');
            }

            const exportedData: Record<string, any> = {
                metadata: {
                    exportDate: new Date().toISOString(),
                    budget: {
                        name: currentBudget.name,
                        currency: currentBudget.currency
                    },
                    period: state.config.period,
                    dateRange: {
                        start: state.config.startDate?.toISOString(),
                        end: state.config.endDate?.toISOString()
                    },
                    collections: state.config.collections
                }
            };

            // Process each collection
            for (const collection of state.config.collections) {
                let filteredData;

                switch (collection) {
                    case 'transactions':
                        filteredData = filterDataByUserAndBudget(transactionState.transactions);
                        filteredData = filterDataByPeriod(filteredData, 'date');
                        filteredData = filteredData.map(enrichTransactionData);
                        exportedData.transactions = {
                            items: filteredData,
                            summary: {
                                totalRevenue: transactionState.totalRevenue,
                                totalSpending: transactionState.totalSpending,
                                networth: transactionState.netWorth,
                                estimatedTaxes: transactionState.taxes,
                                categories: transactionState.topCategories.map(cat => ({
                                    category: getBudgetItemDetails(cat.key)?.name || cat.key,
                                    transactions: cat.value.map(enrichTransactionData)
                                }))
                            }
                        };
                        break;

                    case 'payroll':
                        filteredData = filterDataByUserAndBudget(payrollState.payrollEntries);
                        filteredData = filterDataByPeriod(filteredData, 'paymentDate');
                        filteredData = filteredData.map(enrichPayrollData);
                        exportedData.payroll = {
                            items: filteredData,
                            summary: {
                                totalPayroll: payrollState.totalPayroll,
                                monthlyPayroll: Object.entries(payrollState.monthlyPayroll).reduce(
                                    (acc, [month, amount]) => ({
                                        ...acc,
                                        [month]: {
                                            amount,
                                            formattedAmount: new Intl.NumberFormat('en-US', {
                                                style: 'currency',
                                                currency: 'USD'
                                            }).format(amount)
                                        }
                                    }), 
                                    {}
                                )
                            }
                        };
                        break;

                    case 'taxes':
                        filteredData = filterDataByUserAndBudget(taxState.taxEntries);
                        filteredData = filterDataByPeriod(filteredData, 'dueDate');
                        filteredData = filteredData.map(tax => ({
                            taxType: tax.taxType,
                            name: tax.name,
                            description: tax.description,
                            amount: tax.amount,
                            rate: tax.rate,
                            frequency: tax.frequency,
                            dueDate: tax.dueDate,
                            status: {
                                paid: tax.paid,
                                datePaid: tax.datePaid
                            },
                            account: getAccountDetails(tax.accountId),
                            createdAt: tax.createdAt,
                            updatedAt: tax.updatedAt
                        }));
                        exportedData.taxes = {
                            items: filteredData,
                            summary: {
                                totalTaxes: filteredData.reduce((sum, tax) => sum + tax.amount, 0),
                                upcomingTaxes: filteredData
                                    .filter(tax => !tax.status.paid && new Date(tax.dueDate) > new Date())
                                    .reduce((sum, tax) => sum + tax.amount, 0)
                            }
                        };
                        break;

                    case 'inventory':
                        filteredData = filterDataByUserAndBudget(inventoryState.items);
                        filteredData = filteredData.map(item => ({
                            name: item.name,
                            category: item.category,
                            price: {
                                amount: item.price,
                                formatted: new Intl.NumberFormat('en-US', {
                                    style: 'currency',
                                    currency: 'USD'
                                }).format(item.price)
                            },
                            quantity: item.quantity,
                            value: {
                                amount: item.price * item.quantity,
                                formatted: new Intl.NumberFormat('en-US', {
                                    style: 'currency',
                                    currency: 'USD'
                                }).format(item.price * item.quantity)
                            },
                            createdAt: item.createdAt,
                            updatedAt: item.updatedAt
                        }));
                        exportedData.inventory = {
                            items: filteredData,
                            summary: {
                                totalItems: filteredData.reduce((sum, item) => sum + item.quantity, 0),
                                totalValue: filteredData.reduce((sum, item) => sum + (item.price.amount * item.quantity), 0),
                                categoryCounts: filteredData.reduce((acc, item) => ({
                                    ...acc,
                                    [item.category]: (acc[item.category] || 0) + item.quantity
                                }), {} as Record<string, number>)
                            }
                        };
                        break;

                    case 'invoices':
                        filteredData = filterDataByUserAndBudget(invoiceState.invoices);
                        filteredData = filterDataByPeriod(filteredData, 'dueDate');
                        filteredData = filteredData.map(enrichInvoiceData);
                        exportedData.invoices = {
                            items: filteredData,
                            summary: {
                                totalInvoiced: filteredData.reduce((sum, inv) => sum + inv.amount, 0),
                                statusCounts: filteredData.reduce((acc, inv) => ({
                                    ...acc,
                                    [inv.status]: (acc[inv.status] || 0) + 1
                                }), {} as Record<string, number>),
                                outstandingAmount: filteredData
                                    .filter(inv => inv.status !== 'paid')
                                    .reduce((sum, inv) => sum + inv.amount, 0)
                            }
                        };
                        break;

                    case 'budget':
                        filteredData = budgetState.budgetItems
                            .filter(item => item.createdBy === user.uid)
                            .map(enrichBudgetData);
                        
                        exportedData.budget = {
                            items: filteredData,
                            summary: {
                                total: {
                                    budgeted: budgetState.totalBudget,
                                    assigned: budgetState.assignedBudget,
                                    available: budgetState.availableToBudget
                                },
                                categoryTotals: filteredData.reduce((acc, item) => ({
                                    ...acc,
                                    [item.category]: {
                                        budgeted: (acc[item.category]?.budgeted || 0) + item.amounts.target,
                                        spent: (acc[item.category]?.spent || 0) + item.amounts.spent
                                    }
                                }), {} as Record<string, { budgeted: number; spent: number }>)
                            }
                        };
                        break;
                }
            }

            dispatch({ type: 'SET_EXPORTED_DATA', payload: exportedData });

            if (fileType === 'csv') {
                const csvFiles = [];
                const currentDate = new Date().toISOString().split('T')[0];

                // Create CSV files for each collection
                for (const [collectionName, collectionData] of Object.entries(exportedData)) {
                    if (collectionName === 'metadata') continue;

                    // Create CSV content
                    const csvData = convertToCSV(collectionData.items);

                    // Add to files array
                    csvFiles.push({
                        name: `${collectionName}.csv`,
                        content: csvData
                    });

                    // If collection has summary, add it as separate JSON file
                    if (collectionData.summary) {
                        csvFiles.push({
                            name: `${collectionName}_summary.json`,
                            content: JSON.stringify(collectionData.summary, null, 2)
                        });
                    }
                }

                // Add metadata file
                csvFiles.push({
                    name: 'metadata.json',
                    content: JSON.stringify(exportedData.metadata, null, 2)
                });

                // Create and download ZIP
                const zipFileName = getExportFileName('zip');
                await createAndDownloadZip(csvFiles, zipFileName);
            } else {
                // JSON export
                const jsonContent = JSON.stringify(exportedData, null, 2);
                const blob = new Blob([jsonContent], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = getExportFileName('json');
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }

        } catch (error) {
            console.error('Export error:', error);
            dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to export data' });
            throw error;
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, [
        state.config,
        filterDataByPeriod,
        filterDataByUserAndBudget,
        getExportFileName,
        budgetManagerState.currentBudget,
        transactionState,
        payrollState,
        taxState,
        inventoryState,
        invoiceState,
        budgetState,
        enrichTransactionData,
        enrichInvoiceData,
        enrichPayrollData,
        enrichBudgetData,
        getAccountDetails,
        getBudgetItemDetails
    ]);

    const clearError = useCallback(() => {
        dispatch({ type: 'SET_ERROR', payload: null });
    }, []);

    return (
        <ExportContext.Provider value={{ state, updateConfig, exportData, clearError }}>
            {children}
        </ExportContext.Provider>
    );
}

export function useExport() {
    const context = useContext(ExportContext);
    if (!context) {
        throw new Error('useExport must be used within an ExportProvider');
    }
    return context;
}